import { MountElement, SimulatorLoanConditions } from '@nemuru-eng/checkout-js';
import { EditableField, LenderName, PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { AutomobileItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileItem';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import { GetPricingsService } from '@/components/modules/Application/components/PaymentMethod/_shared/services/GetPricingsService';
import { AutoLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/entities/PaymentMethod/AutoLoanPaymentMethod';
import { useAutoLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useAutoLoanPaymentMethodDetailEntity';
import {
  buildEditFinancingSimulatorBoundaries,
  mapSimulatorConditionsToFinancingFields,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editFinancingSimulator.map';
import { resolveEditFinancingDateOfBirth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/resolveEditFinancingDateOfBirth.map';
import {
  isVehicleAmountWithinRange,
  VehicleAmountRange,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/vehicleAmountRange.map';
import { EditAutoPaymentMethodFormFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';
import {
  mapAutoLoanToDtoOptionsToModificationOptions,
  normalizeDate,
  PaymentMethodModificationOptions,
  toRegistrationDate,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/maps/vehicleModificationPreview.map';
import { useSimulator } from '@/components/shared/hooks/useSimulator';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

const pricingsService = GetPricingsService.getInstance();

export const EDIT_FINANCING_SIMULATOR_MOUNT_ID = 'edit-auto-financing-nemuru-simulator';
const SIMULATOR_REMOUNT_DEBOUNCE_MS = 600;

export interface FinancingConditionsSelection {
  vehicleAmount: string;
  registrationDate: Date | string | null | undefined;
  paymentMethodOptions?: PaymentMethodModificationOptions;
}

interface UseEditFinancingSimulatorParams {
  editableFields: EditableField[];
  financingDefaults: FinancingLiveSummaryData;
  isEnabled: boolean;
  needsFinancingReselection: boolean;
  showVehicleAmountControl: boolean;
  vehicleAmountRange: VehicleAmountRange;
  onFinancingConditionsSelected: (selection: FinancingConditionsSelection) => void;
  lenderName: LenderName | undefined;
}

interface UseEditFinancingSimulatorResult {
  isSimulatorLoading: boolean;
  hasSimulatorMounted: boolean;
  simulatorMountId: string;
}

export const useEditFinancingSimulator = ({
  editableFields,
  financingDefaults,
  isEnabled,
  lenderName,
  needsFinancingReselection,
  showVehicleAmountControl,
  vehicleAmountRange,
  onFinancingConditionsSelected,
}: UseEditFinancingSimulatorParams): UseEditFinancingSimulatorResult => {
  const {
    state: { cart, selectedAgent },
  } = useApplicationContext();
  const autoLoanEntity = useAutoLoanPaymentMethodDetailEntity();
  const dateOfBirth = resolveEditFinancingDateOfBirth(autoLoanEntity?.loan);
  const { simulatorComponent } = useSimulator();
  const { setValue, control } = useFormContext<EditAutoPaymentMethodFormFields>();
  const itemPrice = useWatch({ control, name: 'item.price' });
  const itemRegistrationDate = useWatch({ control, name: 'item.registrationDate' });
  const itemPriceAmount = Number.parseFloat(itemPrice ?? '');
  const stableItemPriceAmount = Number.isNaN(itemPriceAmount) ? null : itemPriceAmount;
  const stableRegistrationDate = normalizeDate(itemRegistrationDate) ?? null;

  const [SimulatorComponent, setSimulatorComponent] = useState<MountElement | null>(null);
  const [isSimulatorLoading, setIsSimulatorLoading] = useState(true);
  const simulatorRef = useRef<MountElement | null>(null);
  const paymentMethodRef = useRef<AutoLoanPaymentMethod | null>(null);
  const mountedVehicleAmountRef = useRef<number | null>(null);
  const mountedRegistrationDateRef = useRef<string | null>(null);
  const simulatorRegistrationDateRef = useRef<Date | null>(null);
  const mountGenerationRef = useRef(0);

  const getPaymentMethodEntity = (): AutoLoanPaymentMethod | null => {
    if (paymentMethodRef.current) {
      return paymentMethodRef.current;
    }

    const paymentMethodEntity = PaymentMethodFactory.byType(PaymentMethodType.AUTO_LOAN);
    if (!(paymentMethodEntity instanceof AutoLoanPaymentMethod)) {
      return null;
    }

    paymentMethodRef.current = paymentMethodEntity;
    return paymentMethodEntity;
  };

  useEffect(() => {
    return () => {
      unmountSimulator();
    };
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      unmountSimulator();
      mountedVehicleAmountRef.current = null;
      mountedRegistrationDateRef.current = null;
      simulatorRegistrationDateRef.current = null;
      return;
    }

    if (!cart || !selectedAgent) {
      return;
    }

    const auto = cart.items.find((item) => item instanceof AutomobileItem) as AutomobileItem | undefined;

    if (!auto) {
      return;
    }

    const parsedPrice = Number.parseFloat(itemPrice ?? '');
    const assetAmount = Number.isNaN(parsedPrice) ? auto.totalPrice : parsedPrice;
    const currentRegistrationDate = normalizeDate(itemRegistrationDate) ?? null;

    if (showVehicleAmountControl && !isVehicleAmountWithinRange(assetAmount, vehicleAmountRange)) {
      return;
    }

    const hasAmountChangedFromMounted = mountedVehicleAmountRef.current !== assetAmount;
    const hasRegistrationDateChangedFromMounted = mountedRegistrationDateRef.current !== currentRegistrationDate;
    const shouldRemount = !simulatorRef.current || hasAmountChangedFromMounted || hasRegistrationDateChangedFromMounted;

    if (!shouldRemount || assetAmount <= 0) {
      return;
    }

    const debounceMs = needsFinancingReselection || hasAmountChangedFromMounted ? SIMULATOR_REMOUNT_DEBOUNCE_MS : 0;

    const timeoutId = setTimeout(() => {
      void mountSimulator();
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isEnabled,
    stableItemPriceAmount,
    stableRegistrationDate,
    needsFinancingReselection,
    showVehicleAmountControl,
    vehicleAmountRange.min,
    vehicleAmountRange.max,
  ]);

  const mountSimulator = async () => {
    if (!cart || !selectedAgent) {
      return;
    }

    const auto = cart.items.find((item) => item instanceof AutomobileItem) as AutomobileItem | undefined;

    if (!auto) {
      return;
    }

    const parsedPrice = Number.parseFloat(itemPrice ?? '');
    const assetAmount = Number.isNaN(parsedPrice) ? auto.totalPrice : parsedPrice;

    if (assetAmount <= 0) {
      return;
    }

    if (showVehicleAmountControl && !isVehicleAmountWithinRange(assetAmount, vehicleAmountRange)) {
      return;
    }

    setIsSimulatorLoading(true);

    const mountGeneration = ++mountGenerationRef.current;
    const registrationDate =
      toRegistrationDate(itemRegistrationDate) ?? toRegistrationDate(auto.registrationDate) ?? null;
    const normalizedRegistrationDate = normalizeDate(registrationDate) ?? null;
    simulatorRegistrationDateRef.current = registrationDate;
    const downPaymentAmount = financingDefaults.downPaymentAmount ?? 0;
    const discountAmount = financingDefaults.discountAmount ?? 0;
    const principal = assetAmount - downPaymentAmount - discountAmount;

    const boundaries = buildEditFinancingSimulatorBoundaries({
      editableFields,
      lenderName,
      assetAmount,
      registrationDate,
      financingDefaults,
      principal,
      dateOfBirth,
    });

    unmountSimulator();
    getPaymentMethodEntity()?.selectSimulatorConditions(null);

    try {
      await pricingsService.execute(selectedAgent.id);
    } catch (error) {
      console.error('Failed to fetch pricings', error);
    }

    const Simulator = await simulatorComponent(
      {
        options: {
          disableLocaleSelector: true,
          disableSelectedCard: true,
        },
        boundaries,
        onSelected: handleConditionsSelection,
        onError: (error) => {
          console.log('-> onError', error);
        },
      },
      selectedAgent,
    );

    if (mountGeneration !== mountGenerationRef.current) {
      return;
    }

    if (!Simulator) {
      setIsSimulatorLoading(false);
      return;
    }

    simulatorRef.current = Simulator;
    mountedVehicleAmountRef.current = assetAmount;
    mountedRegistrationDateRef.current = normalizedRegistrationDate;
    setSimulatorComponent(Simulator);
    Simulator.mount(`#${EDIT_FINANCING_SIMULATOR_MOUNT_ID}`);
    setIsSimulatorLoading(false);
  };

  const unmountSimulator = () => {
    simulatorRef.current?.unmount();
    simulatorRef.current = null;
    setSimulatorComponent(null);
  };

  const handleConditionsSelection = (simulatorConditions: SimulatorLoanConditions[] | null): void => {
    if (needsFinancingReselection && !simulatorConditions?.length) {
      return;
    }

    const financingFields = mapSimulatorConditionsToFinancingFields(simulatorConditions);
    const selectedCondition = simulatorConditions?.[0];

    if (!financingFields) {
      return;
    }

    const paymentMethodEntity = getPaymentMethodEntity();
    paymentMethodEntity?.selectSimulatorConditions(simulatorConditions);

    const previousPrice = itemPrice;
    const confirmedVehicleAmount =
      selectedCondition?.assetAmount?.value !== undefined
        ? String(selectedCondition.assetAmount.value)
        : itemPrice ?? '';
    const previousParsed = Number.parseFloat(previousPrice ?? '');
    const confirmedParsed = Number.parseFloat(confirmedVehicleAmount);
    const willUpdatePrice =
      selectedCondition?.assetAmount?.value !== undefined &&
      !Number.isNaN(confirmedParsed) &&
      previousParsed !== confirmedParsed;

    setValue('financing', financingFields, { shouldDirty: true, shouldValidate: true });

    if (willUpdatePrice) {
      mountedVehicleAmountRef.current = confirmedParsed;
      setValue('item.price', confirmedVehicleAmount, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    const pricings = pricingsService.pricings;
    const simulatorRegistrationDate = simulatorRegistrationDateRef.current;
    const paymentMethodOptions =
      paymentMethodEntity?.selectedConditionsGroup && cart && pricings
        ? mapAutoLoanToDtoOptionsToModificationOptions(paymentMethodEntity.toDto(pricings, cart).options, {
            annualInterest: selectedCondition?.annualInterest?.value,
            registrationDate: simulatorRegistrationDate,
          })
        : undefined;

    if (confirmedVehicleAmount) {
      onFinancingConditionsSelected({
        vehicleAmount: confirmedVehicleAmount,
        registrationDate: simulatorRegistrationDate,
        paymentMethodOptions,
      });
    }
  };

  return {
    isSimulatorLoading,
    hasSimulatorMounted: Boolean(SimulatorComponent),
    simulatorMountId: EDIT_FINANCING_SIMULATOR_MOUNT_ID,
  };
};
